/**
 * prices-page-logic.js — Pure functions for the /prices landing page.
 * Works in both browser (window.PricesPageLogic) and Node.js (module.exports).
 */
(function(exports) {
  'use strict';

  // ── Store color map ──────────────────────────────────────────────────
  var STORE_COLORS = {
    'iNutrition':       '#e67e22',
    'HK Health Mall':   '#e91e63',
    'Eden Home':        '#8e44ad',
    'GoGoHealth':       '#2980b9',
    'Codex Genetics':   '#c0392b',
    'NutriHealth':      '#16a085',
    'Culture Homes':    '#d35400',
    'Tai Ho Pai':       '#7f8c8d',
    'Healthy Living':   '#f39c12',
    'Nestle Shop':      '#1abc9c',
    'HK Senior Mall':   '#34495e',
    'LiveSmart':        '#9b59b6',
    'WLMA':             '#95a5a6',
    'GoHealth Store':   '#3498db',
    'Soul Small':       '#e74c3c',
    'Best Offer HK':    '#27ae60',
    'eShop Nurse':      '#d68910',
    'Sunyee Mall':      '#5d6d7e',
    'HKTVMall':         '#cb4335',
    'Rehab Express':    '#6c3483',
    'Elderly Careshop': '#a04000',
    'HKFCP':            '#2c3e50'
  };

  // ── Levenshtein distance ─────────────────────────────────────────────
  function levenshtein(a, b) {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    var matrix = [];
    var i, j, cost;
    for (i = 0; i <= b.length; i++) matrix[i] = [i];
    for (j = 0; j <= a.length; j++) matrix[0][j] = j;
    for (i = 1; i <= b.length; i++) {
      for (j = 1; j <= a.length; j++) {
        cost = b[i - 1] === a[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }
    return matrix[b.length][a.length];
  }

  // ── Fuzzy match ──────────────────────────────────────────────────────
  // Returns { match: bool, score: number }
  // score 0 = substring, 1 = word prefix, 2 = levenshtein (≤2 edits)
  function fuzzyMatch(query, text) {
    var q = query.toLowerCase();
    var t = text.toLowerCase();
    // Exact substring match
    if (t.indexOf(q) >= 0) return { match: true, score: 0 };
    // Check individual words
    var words = t.split(/\s+/);
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      if (!w) continue;
      if (w.indexOf(q) === 0) return { match: true, score: 1 };
      // Reverse prefix ("souvenaid 48" starts with the word "souvenaid") only for real words.
      // A one-letter token such as the "x" in "24克 x 30包" would otherwise match every query
      // that begins with it. CJK words carry meaning at two characters, Latin ones at three.
      var minReverseLen = /[㐀-鿿]/.test(w) ? 2 : 3;
      if (w.length >= minReverseLen && q.indexOf(w) === 0) return { match: true, score: 1 };
      if (q.length >= 3 && levenshtein(q, w.slice(0, q.length + 2)) <= 2) return { match: true, score: 2 };
    }
    return { match: false, score: 999 };
  }

  // ── Search matching ──────────────────────────────────────────────────
  // products: array of { id, searchText, ... }
  // Returns matched products sorted by score. limit defaults to 5 (the dropdown);
  // pass 0 for every match (filtering the card grid).
  function getSearchMatches(query, products, limit) {
    if (!query) return [];
    var results = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var m = fuzzyMatch(query, p.searchText);
      if (m.match) results.push({ product: p, score: m.score });
    }
    results.sort(function(a, b) { return a.score - b.score; });
    var max = (limit === undefined || limit === null) ? 5 : limit;
    return max > 0 ? results.slice(0, max) : results;
  }

  function getMonitoredHandles(monitoredVariants, groupId) {
    var handles = [];
    if (!monitoredVariants || !groupId) return handles;

    for (var i = 0; i < monitoredVariants.length; i++) {
      var variant = monitoredVariants[i];
      if (variant && variant.groupId === groupId && variant.handle) {
        handles.push(variant.handle);
      }
    }

    return handles;
  }

  function buildPricesPageProducts(familyConfigs, monitoredVariants, familyAssets) {
    var products = [];
    var assetsById = familyAssets || {};

    if (!familyConfigs) return products;

    for (var i = 0; i < familyConfigs.length; i++) {
      var family = familyConfigs[i];
      var assets = assetsById[family.id] || {};

      products.push({
        id: family.id,
        brand: family.brand,
        name: family.name,
        sub: family.sub,
        searchTerms: family.searchTerms,
        unit: family.unit,
        link: family.link,
        handle: family.handle,
        handles: getMonitoredHandles(monitoredVariants, family.id),
        image: assets.image,
        metafield: assets.metafield,
        history: decodeProductHistory(assets.history)
      });
    }

    return products;
  }

  function localizePath(path, localeCode) {
    if (!path || !localeCode) return path;

    var normalizedLocale = String(localeCode).toLowerCase();
    if (normalizedLocale !== 'en' && normalizedLocale.indexOf('en-') !== 0) {
      return path;
    }

    if (path.indexOf('/en/') === 0 || path === '/en') {
      return path;
    }

    if (path.charAt(0) !== '/') {
      return path;
    }

    return '/en' + path;
  }

  function appendQueryParam(path, key, value) {
    if (!path) return path;

    var hash = '';
    var basePath = path;
    var hashIndex = path.indexOf('#');
    if (hashIndex >= 0) {
      hash = path.slice(hashIndex);
      basePath = path.slice(0, hashIndex);
    }

    var encodedKey = encodeURIComponent(key);
    var encodedValue = encodeURIComponent(value);
    var queryPattern = new RegExp('([?&])' + encodedKey + '=[^&]*');

    if (queryPattern.test(basePath)) {
      basePath = basePath.replace(queryPattern, '$1' + encodedKey + '=' + encodedValue);
    } else {
      basePath += (basePath.indexOf('?') >= 0 ? '&' : '?') + encodedKey + '=' + encodedValue;
    }

    return basePath + hash;
  }

  function getPreferredPackLink(product, packKey, localeCode) {
    var metafield = product && product.metafield;
    var packData = metafield && metafield.packs && metafield.packs[packKey];
    var ourPrice = packData && packData.ourPrice;

    if (ourPrice && ourPrice.virtual) {
      return localizePath(appendQueryParam(product.link, 'pack', packKey), localeCode);
    }

    if (metafield && metafield.packLinks && metafield.packLinks[packKey]) {
      return localizePath(metafield.packLinks[packKey], localeCode);
    }

    if (metafield && metafield.packHandles && metafield.packHandles[packKey]) {
      return localizePath(appendQueryParam('/products/' + metafield.packHandles[packKey] + '#price-comparison', 'pack', packKey), localeCode);
    }

    return localizePath(appendQueryParam(product.link, 'pack', packKey), localeCode);
  }

  // The pack's product page with its anchor swapped: '' for the page top (card image and
  // name), 'buy' for the buy box (the widget script scrolls #buy to the product info).
  function packPageLink(product, packKey, localeCode, anchor) {
    var link = getPreferredPackLink(product, packKey, localeCode);
    if (!link) return link;
    var base = link.split('#')[0];
    return anchor ? base + '#' + anchor : base;
  }

  // ── Sharded shop metafields ──────────────────────────────────────────
  // Shopify caps one json metafield at 131,072 bytes (API 2026-04+), so the
  // scraper writes the snapshot across prices_page_snapshot_0..N and the chart
  // history, run-length encoded, across prices_page_history_0..N.

  function mergePricesPageSnapshotShards(shards) {
    if (!shards || !shards.length) return null;

    var families = {};
    var generatedAt = null;
    var version = null;
    var found = false;

    for (var i = 0; i < shards.length; i++) {
      var shard = shards[i];
      if (!shard || !shard.families) continue;

      if (!generatedAt && shard.generatedAt) generatedAt = shard.generatedAt;
      if (version === null && shard.version) version = shard.version;

      for (var familyId in shard.families) {
        if (!Object.prototype.hasOwnProperty.call(shard.families, familyId)) continue;
        families[familyId] = shard.families[familyId];
        found = true;
      }
    }

    if (!found) return null;

    return { version: version || 1, generatedAt: generatedAt, families: families };
  }

  // Expand [value, count] runs back into one value per day.
  function decodeHistoryDoc(doc) {
    if (!doc || !doc.families) return {};

    var stores = doc.stores || [];
    var out = {};

    for (var familyId in doc.families) {
      if (!Object.prototype.hasOwnProperty.call(doc.families, familyId)) continue;

      var encoded = doc.families[familyId];
      var packs = {};
      var encodedPacks = (encoded && encoded.p) || {};

      for (var packKey in encodedPacks) {
        if (!Object.prototype.hasOwnProperty.call(encodedPacks, packKey)) continue;

        var encodedPack = encodedPacks[packKey] || {};
        var packStores = {};

        for (var index in encodedPack) {
          if (!Object.prototype.hasOwnProperty.call(encodedPack, index)) continue;

          var storeName = stores[Number(index)];
          if (storeName === undefined) continue;

          var runs = encodedPack[index] || [];
          var series = [];

          for (var r = 0; r < runs.length; r++) {
            for (var c = 0; c < runs[r][1]; c++) series.push(runs[r][0]);
          }

          packStores[storeName] = series;
        }

        packs[packKey] = { stores: packStores };
      }

      out[familyId] = { startDate: encoded.s, endDate: encoded.e, packs: packs };
    }

    return out;
  }

  // A history value can arrive raw (legacy metafield) or run-length encoded.
  // Normalising here means every caller downstream sees the raw shape.
  function decodeProductHistory(value) {
    // Return the value itself when there is nothing to decode, so an absent
    // history stays absent rather than becoming null.
    if (!value) return value;
    if (value.v !== 2) return value;

    var family = {};
    for (var key in value) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
      if (key === 'v' || key === 'stores') continue;
      family[key] = value[key];
    }

    return decodeHistoryDoc({ v: value.v, stores: value.stores, families: { h: family } }).h;
  }

  function mergeHistoryShards(docs) {
    var merged = {};

    for (var i = 0; i < (docs || []).length; i++) {
      var decoded = decodeHistoryDoc(docs[i]);

      for (var familyId in decoded) {
        if (!Object.prototype.hasOwnProperty.call(decoded, familyId)) continue;
        merged[familyId] = decoded[familyId];
      }
    }

    return merged;
  }

  function buildFamilyAssetsFromSnapshot(pricesPageSnapshot, historiesById) {
    var assetsById = {};
    var families = pricesPageSnapshot && pricesPageSnapshot.families;
    var histories = historiesById || {};

    if (!families) return assetsById;

    for (var familyId in families) {
      if (!Object.prototype.hasOwnProperty.call(families, familyId)) continue;

      var family = families[familyId];

      assetsById[familyId] = {
        image: family && family.image,
        metafield: family && family.metafield,
        // Prefer the separate history document; fall back to a copy embedded
        // in the snapshot so the page still charts off the legacy metafield.
        history: histories[familyId] || (family && family.history) || null
      };
    }

    return assetsById;
  }

  // ── Store ranking for comparison table ───────────────────────────────
  // Input: metafield pack object { ourPrice, competitors }
  // Output: { stores: [{ name, pu, ours, virtual, diff, rank, staleness }], oursIdx, ourPu, isCheapest }
  function buildStoreRanking(packData) {
    var stores = [];
    var hasVirtualOurPrice = !!(packData.ourPrice && packData.ourPrice.virtual);

    // iNutrition
    if (packData.ourPrice) {
      var inuPu = packData.ourPrice.perUnitWithShipping || packData.ourPrice.perBottle || 0;
      stores.push({
        name: 'iNutrition',
        pu: inuPu,
        ours: true,
        virtual: !!packData.ourPrice.virtual
      });
    }

    // Competitors (exclude expired)
    var comps = packData.competitors || [];
    for (var i = 0; i < comps.length; i++) {
      var comp = comps[i];
      if (comp.staleness === 'expired') continue;
      stores.push({
        name: comp.store,
        pu: comp.perUnitWithShipping || comp.perBottle || 0,
        ours: false,
        staleness: comp.staleness || 'current'
      });
    }

    // Sort by per-unit price ascending
    stores.sort(function(a, b) { return a.pu - b.pu; });

    // Find iNutrition's position
    var oursIdx = -1;
    var ourPu = null;
    for (var j = 0; j < stores.length; j++) {
      if (stores[j].ours) {
        oursIdx = j;
        ourPu = hasVirtualOurPrice ? null : stores[j].pu;
        break;
      }
    }

    // Compute diff and rank
    for (var k = 0; k < stores.length; k++) {
      stores[k].rank = k + 1;
      if (stores[k].ours) {
        stores[k].diff = null;
      } else if (ourPu !== null) {
        stores[k].diff = Math.round((stores[k].pu - ourPu) * 100) / 100;
      } else {
        stores[k].diff = null;
      }
    }

    // isCheapest: rank 1 AND not virtual
    var isCheapest = oursIdx === 0 && !stores[0].virtual;

    return {
      stores: stores,
      oursIdx: oursIdx,
      ourPu: ourPu,
      isCheapest: isCheapest
    };
  }

  // ── Pack label helpers ───────────────────────────────────────────────
  function packLabel(packKey, unit) {
    var num = packKey.replace(/\D/g, '');
    return num + unit;
  }

  function packLabelFull(packKey, unit) {
    var num = packKey.replace(/\D/g, '');
    return num + unit + '裝';
  }

  // ── Chart data helpers ────────────────────────────────────────────────

  /**
   * Parse a 'YYYY-MM-DD' date string into a Date object (treated as UTC).
   * @param {string} s
   * @returns {Date}
   */
  function parseDate(s) {
    var parts = s.split('-');
    return new Date(Date.UTC(+parts[0], +parts[1] - 1, +parts[2]));
  }

  /**
   * Format a Date (UTC) to a short Chinese label, e.g. '4月8日'.
   * @param {Date} d
   * @returns {string}
   */
  // Tooltip date in the page language (audit #6): '9月14日' on the Chinese page, '14 Sep' on /en.
  var EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function formatDateLabel(d, english) {
    if (english) return d.getUTCDate() + ' ' + EN_MONTHS[d.getUTCMonth()];
    return (d.getUTCMonth() + 1) + '月' + d.getUTCDate() + '日';
  }

  /**
   * Format a Date (UTC) to a compact label for axis, e.g. '4/8'.
   * @param {Date} d
   * @returns {string}
   */
  function formatDateShort(d) {
    return (d.getUTCMonth() + 1) + '/' + d.getUTCDate();
  }

  /**
   * Generate an array of date strings (YYYY-MM-DD) from startDate to endDate.
   * @param {string} startDate - 'YYYY-MM-DD'
   * @param {string} endDate   - 'YYYY-MM-DD'
   * @returns {string[]}
   */
  function dateRange(startDate, endDate) {
    var dates = [];
    var cur = parseDate(startDate);
    var end = parseDate(endDate);
    while (cur <= end) {
      var y = cur.getUTCFullYear();
      var m = ('0' + (cur.getUTCMonth() + 1)).slice(-2);
      var d = ('0' + cur.getUTCDate()).slice(-2);
      dates.push(y + '-' + m + '-' + d);
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return dates;
  }

  /**
   * Slice history data for a specific pack and date range.
   *
   * @param {Object|null} history  - The price_history metafield value
   * @param {string} packKey       - e.g. '24'
   * @param {number} days          - number of trailing days (30, 90, 180, 365)
   * @returns {Object|null} { dates: string[], stores: { name: string, values: (number|null)[] }[], minVal: number, maxVal: number }
   */
  function sliceHistory(history, packKey, days) {
    if (!history || !history.packs || !history.packs[packKey]) return null;

    var pack = history.packs[packKey];
    var storeNames = Object.keys(pack.stores);
    if (storeNames.length === 0) return null;

    // Generate full date range
    var allDates = dateRange(history.startDate, history.endDate);
    if (allDates.length === 0) return null;

    var maxSeriesLength = 0;
    for (var seriesIdx = 0; seriesIdx < storeNames.length; seriesIdx++) {
      var series = pack.stores[storeNames[seriesIdx]];
      var seriesLength = Array.isArray(series) ? series.length : 0;
      if (seriesLength > maxSeriesLength) maxSeriesLength = seriesLength;
    }
    if (maxSeriesLength === 0) return null;

    var alignedDates = allDates;
    if (maxSeriesLength !== allDates.length) {
      // Defensively align malformed histories to the newest available samples.
      alignedDates = allDates.slice(Math.max(0, allDates.length - maxSeriesLength));
    }

    // Slice trailing N days
    var startIdx = Math.max(0, alignedDates.length - days);
    var dates = alignedDates.slice(startIdx);

    var stores = [];
    var minVal = Infinity;
    var maxVal = -Infinity;

    for (var i = 0; i < storeNames.length; i++) {
      var name = storeNames[i];
      var fullArr = Array.isArray(pack.stores[name]) ? pack.stores[name] : [];
      if (fullArr.length < alignedDates.length) {
        fullArr = new Array(alignedDates.length - fullArr.length).fill(null).concat(fullArr);
      } else if (fullArr.length > alignedDates.length) {
        fullArr = fullArr.slice(fullArr.length - alignedDates.length);
      }
      var values = fullArr.slice(startIdx);
      stores.push({ name: name, values: values });

      for (var j = 0; j < values.length; j++) {
        if (values[j] !== null && values[j] !== undefined) {
          if (values[j] < minVal) minVal = values[j];
          if (values[j] > maxVal) maxVal = values[j];
        }
      }
    }

    if (minVal === Infinity) return null;

    return { dates: dates, stores: stores, minVal: minVal, maxVal: maxVal };
  }

  var OUTLIER_IQR = 3;
  var OUTLIER_MIN_RATIO = 1.4;
  // A store is spiking (pinnable) when at most this share of its points are off the scale.
  var OUTLIER_SPIKE_SHARE = 0.5;

  // Linear-interpolated quantile of an ascending array.
  function quantile(sorted, q) {
    var pos = (sorted.length - 1) * q;
    var lo = Math.floor(pos);
    var hi = Math.ceil(pos);
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
  }

  /**
   * Compute SVG chart data (polyline points and hover columns).
   *
   * @param {Object} sliced  - output of sliceHistory()
   * @param {number} width   - SVG viewport width
   * @param {number} height  - SVG viewport height
   * @param {Object} padding - { top, right, bottom, left }
   * @returns {Object} { lines: [{ name, points, segments }], yMin, yMax, yMid, xDates, plotArea }
   */
  function computeChartLayout(sliced, width, height, padding) {
    var pTop = padding.top || 0;
    var pRight = padding.right || 0;
    var pBottom = padding.bottom || 0;
    var pLeft = padding.left || 0;

    var plotW = width - pLeft - pRight;
    var plotH = height - pTop - pBottom;

    // Outliers (plan 2.2, audit #5): one spike (e.g. a store briefly at $141 against ~$78)
    // stretched the scale and squashed every other line into one band. A price is off the
    // scale when it sits far above the usual spread: above Q3 + OUTLIER_IQR x IQR of every
    // price in the window, and at least OUTLIER_MIN_RATIO x the median (so a store a little
    // dearer than tightly clustered rivals is never pinned). Off-scale points are drawn at
    // the top edge and flagged (point.capped); the range is set from the rest; point.val
    // keeps the real price for the tooltip. (The plan's first idea, 2x the median, would not
    // have caught the audit's own example: 141 is only 1.8x a ~$78 median.)
    var allVals = [];
    for (var ov = 0; ov < sliced.stores.length; ov++) {
      var sv = sliced.stores[ov].values;
      for (var ow = 0; ow < sv.length; ow++) {
        if (sv[ow] !== null && sv[ow] !== undefined) allVals.push(sv[ow]);
      }
    }
    var cap = Infinity;
    var rangeMax = sliced.maxVal;
    var keepScale = [];
    if (allVals.length) {
      var sortedVals = allVals.slice().sort(function(a, b) { return a - b; });
      var median = quantile(sortedVals, 0.5);
      var q1 = quantile(sortedVals, 0.25);
      var q3 = quantile(sortedVals, 0.75);
      var limit = Math.max(q3 + OUTLIER_IQR * (q3 - q1), median * OUTLIER_MIN_RATIO);
      if (median > 0 && sliced.maxVal > limit) {
        // Only temporary spikes are pinned. A store above the limit on most of its points in
        // the window is just dearer; it keeps its true scale so its level reads honestly.
        rangeMax = sliced.minVal;
        var anySpike = false;
        for (var os = 0; os < sliced.stores.length; os++) {
          var vals = sliced.stores[os].values;
          var seen = 0;
          var above = 0;
          for (var ox = 0; ox < vals.length; ox++) {
            if (vals[ox] === null || vals[ox] === undefined) continue;
            seen++;
            if (vals[ox] > limit) above++;
          }
          var isLevel = above > 0 && above > seen * OUTLIER_SPIKE_SHARE;
          keepScale[os] = isLevel;
          if (above > 0 && !isLevel) anySpike = true;
          for (var oy = 0; oy < vals.length; oy++) {
            var vv = vals[oy];
            if (vv === null || vv === undefined) continue;
            if ((isLevel || vv <= limit) && vv > rangeMax) rangeMax = vv;
          }
        }
        if (anySpike) {
          cap = limit;
        } else {
          rangeMax = sliced.maxVal;
        }
      }
    }

    // Add 2% padding to Y range to avoid lines touching edges
    var yRange = rangeMax - sliced.minVal;
    if (yRange < 0.01) yRange = 1; // flat line protection
    var yPad = yRange * 0.02;
    var yMin = sliced.minVal - yPad;
    var yMax = rangeMax + yPad;
    var yMid = (yMin + yMax) / 2;

    var numDates = sliced.dates.length;
    var xStep = numDates > 1 ? plotW / (numDates - 1) : 0;

    function toX(idx) { return pLeft + idx * xStep; }
    function toY(val) { return pTop + plotH - ((val - yMin) / (yMax - yMin)) * plotH; }

    var lines = [];
    for (var s = 0; s < sliced.stores.length; s++) {
      var store = sliced.stores[s];
      var points = [];
      var segments = []; // contiguous non-null segments as polyline strings

      var currentSeg = [];
      for (var d = 0; d < store.values.length; d++) {
        var v = store.values[d];
        if (v !== null && v !== undefined) {
          var px = toX(d);
          var capped = !keepScale[s] && v > cap;
          var py = capped ? pTop : toY(v);
          points.push({ x: px, y: py, val: v, idx: d, capped: capped });
          currentSeg.push(px + ',' + py);
        } else {
          points.push(null);
          if (currentSeg.length > 0) {
            segments.push(currentSeg.join(' '));
            currentSeg = [];
          }
        }
      }
      if (currentSeg.length > 0) {
        segments.push(currentSeg.join(' '));
      }

      lines.push({ name: store.name, points: points, segments: segments });
    }

    return {
      lines: lines,
      yMin: yMin,
      yMax: yMax,
      cap: cap === Infinity ? null : cap,
      yMid: yMid,
      xDates: sliced.dates,
      plotArea: { x: pLeft, y: pTop, w: plotW, h: plotH },
      toX: toX,
      toY: toY
    };
  }

  /**
   * Build a tooltip data object for a given day index.
   * Returns { date, dateLabel, entries: [{ name, value, color }] } sorted by price.
   */
  function getTooltipData(layout, dayIdx, storeColors, english) {
    var dateStr = layout.xDates[dayIdx];
    var dt = parseDate(dateStr);
    var label = formatDateLabel(dt, english);

    var entries = [];
    for (var i = 0; i < layout.lines.length; i++) {
      var line = layout.lines[i];
      var pt = line.points[dayIdx];
      if (pt) {
        entries.push({
          name: line.name,
          value: pt.val,
          color: storeColors[line.name] || '#999'
        });
      }
    }

    entries.sort(function(a, b) { return a.value - b.value; });

    return { date: dateStr, dateLabel: label, entries: entries };
  }

  // ── Exports ──────────────────────────────────────────────────────────
  exports.STORE_COLORS = STORE_COLORS;
  exports.levenshtein = levenshtein;
  exports.fuzzyMatch = fuzzyMatch;
  exports.getSearchMatches = getSearchMatches;
  exports.getMonitoredHandles = getMonitoredHandles;
  exports.buildPricesPageProducts = buildPricesPageProducts;
  exports.getPreferredPackLink = getPreferredPackLink;
  exports.packPageLink = packPageLink;
  exports.buildFamilyAssetsFromSnapshot = buildFamilyAssetsFromSnapshot;
  exports.mergePricesPageSnapshotShards = mergePricesPageSnapshotShards;
  exports.decodeHistoryDoc = decodeHistoryDoc;
  exports.mergeHistoryShards = mergeHistoryShards;
  exports.decodeProductHistory = decodeProductHistory;
  // Filter out virtual iNutrition entries and re-rank
  function filterVirtualStores(stores) {
    var filtered = [];
    for (var i = 0; i < stores.length; i++) {
      if (stores[i].ours && stores[i].virtual) continue;
      filtered.push(stores[i]);
    }
    for (var j = 0; j < filtered.length; j++) { filtered[j].rank = j + 1; }
    return filtered;
  }

  // ── Pagination ────────────────────────────────────────────────────
  var PAGE_SIZE = 10;

  // ── Money ─────────────────────────────────────────────────────────────
  // One display format for HKD amounts: HK$1,234.50.
  function formatHkd(amount) {
    if (amount === null || amount === undefined || !isFinite(amount)) return '';
    return (amount < 0 ? '-' : '') + 'HK$' + formatAmount(Math.abs(amount));
  }

  // Amount with thousands separators and 2 decimals, no currency: 6000 -> '6,000.00'.
  function formatAmount(amount) {
    var parts = Math.abs(amount).toFixed(2).split('.');
    return (amount < 0 ? '-' : '') + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
  }

  // Our cheapest per-unit price across the packs we sell (virtual packs excluded), for the
  // search suggestions' "from HK$X / unit" line (plan 2.5). null when we sell none.
  function lowestOurPerUnit(metafield) {
    var best = null;
    var packs = (metafield && metafield.packs) || {};
    for (var key in packs) {
      if (!Object.prototype.hasOwnProperty.call(packs, key)) continue;
      var ours = packs[key] && packs[key].ourPrice;
      if (!ours || ours.virtual) continue;
      var pu = ours.perUnitWithShipping || ours.perBottle;
      if (pu && (best === null || pu < best)) best = pu;
    }
    return best;
  }

  // ── Our standing in one pack (plan 1.5) ───────────────────────────────
  // 'lowest' when every live competitor is at least 1 cent per unit dearer (the table's
  // "Same" tolerance), 'tied' when the cheapest one matches us, '' otherwise, including a
  // virtual pack, no competitors, or a floored Nestlé line we no longer undercut.
  // saving (lowest only) = cheapest other store's total − ours, from the totals the table
  // shows when both exist, else the per-unit gap × pack units.
  function packStanding(packData, packKey) {
    var none = { status: '', saving: null };
    var ourPrice = packData && packData.ourPrice;
    if (!ourPrice || ourPrice.virtual) return none;
    var ourPu = ourPrice.perUnitWithShipping || ourPrice.perBottle;
    if (!ourPu) return none;

    var cheapest = null;
    var comps = packData.competitors || [];
    for (var i = 0; i < comps.length; i++) {
      if (comps[i].staleness === 'expired') continue;
      var pu = comps[i].perUnitWithShipping || comps[i].perBottle;
      if (!pu) continue;
      if (!cheapest || pu < cheapest.pu) cheapest = { pu: pu, comp: comps[i] };
    }
    if (!cheapest) return none;

    var gapCents = Math.round((cheapest.pu - ourPu) * 100);
    if (gapCents === 0) return { status: 'tied', saving: null };
    if (gapCents < 0) return none;

    var ourTotal = ourPrice.totalWithShipping || ourPrice.price;
    var otherTotal = cheapest.comp.totalWithShipping || cheapest.comp.price;
    var saving;
    if (ourTotal && otherTotal) {
      saving = otherTotal - ourTotal;
    } else {
      saving = (cheapest.pu - ourPu) * (parseInt(packKey, 10) || 0);
    }
    saving = Math.round(saving * 100) / 100;
    return { status: 'lowest', saving: saving > 0 ? saving : null };
  }

  // ── Comparison table row cap ─────────────────────────────────────────
  // Store tables show this many rows; the rest open behind "Show all".
  var TABLE_VISIBLE_ROWS = 5;

  // stores: ranked rows ({ ours: bool, ... }), cheapest first.
  // Returns { visible: [bool per row], count } — the first `limit` rows, plus our row
  // wherever it ranks, so iNutrition never hides behind the toggle.
  function visibleStoreRows(stores, limit) {
    var visible = [];
    var count = 0;
    for (var i = 0; i < (stores || []).length; i++) {
      var show = i < limit || !!stores[i].ours;
      visible.push(show);
      if (show) count++;
    }
    return { visible: visible, count: count };
  }

  function getPageCount(total, pageSize) {
    var size = pageSize || PAGE_SIZE;
    return Math.max(1, Math.ceil((total || 0) / size));
  }

  // Coerce any ?page= value into a page that exists, falling back to 1
  function clampPage(page, pageCount) {
    var n = parseInt(page, 10);
    if (!n || n < 1) return 1;
    return Math.min(n, pageCount);
  }

  // Page numbers to draw, with 'gap' where a run is collapsed into an ellipsis.
  // Always keeps the first page, the last page, and one page either side of the
  // current one; seven or fewer pages are all shown.
  function buildPageList(current, pageCount) {
    var pages = [];
    var i;
    if (pageCount <= 7) {
      for (i = 1; i <= pageCount; i++) pages.push(i);
      return pages;
    }
    var start = Math.max(2, current - 1);
    var end = Math.min(pageCount - 1, current + 1);
    // Near either end, widen the window so the bar keeps a steady length
    if (current <= 3) { start = 2; end = 4; }
    if (current >= pageCount - 2) { start = pageCount - 3; end = pageCount - 1; }
    // An ellipsis standing in for a single page is worse than the page itself
    if (start === 3) start = 2;
    if (end === pageCount - 2) end = pageCount - 1;
    pages.push(1);
    if (start > 2) pages.push('gap');
    for (i = start; i <= end; i++) pages.push(i);
    if (end < pageCount - 1) pages.push('gap');
    pages.push(pageCount);
    return pages;
  }

  exports.PAGE_SIZE = PAGE_SIZE;
  exports.TABLE_VISIBLE_ROWS = TABLE_VISIBLE_ROWS;
  exports.formatHkd = formatHkd;
  exports.formatAmount = formatAmount;
  exports.lowestOurPerUnit = lowestOurPerUnit;
  exports.formatDateLabel = formatDateLabel;
  exports.packStanding = packStanding;
  exports.visibleStoreRows = visibleStoreRows;
  exports.getPageCount = getPageCount;
  exports.clampPage = clampPage;
  exports.buildPageList = buildPageList;

  exports.buildStoreRanking = buildStoreRanking;
  exports.filterVirtualStores = filterVirtualStores;
  exports.packLabel = packLabel;
  exports.packLabelFull = packLabelFull;
  exports.parseDate = parseDate;
  exports.formatDateLabel = formatDateLabel;
  exports.formatDateShort = formatDateShort;
  exports.dateRange = dateRange;
  exports.sliceHistory = sliceHistory;
  exports.computeChartLayout = computeChartLayout;
  exports.getTooltipData = getTooltipData;

})(typeof module !== 'undefined' && module.exports ? module.exports : (window.PricesPageLogic = {}));
